import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type { components } from "@oathera/contract/api";
import type { Service } from "@oathera/contract";
import { DenseTable, estimateSuffix, type Column } from "../../components/index.ts";
import { quantityMeasure, ROUTE_SIGNALS, type GoldenSignalSpec } from "./vocab.ts";

/**
 * The per-route table (task 11.5; design 12.2 "per-route table"; 12.3, 12.4).
 *
 * Each row is a route the service serves with its golden-signal numbers (traffic, errors, p95
 * latency, saturation), rendered through the semantic `DenseTable`. Per design 12.3 the numbers
 * drill down: the route cell links to the events explorer filtered to this service and route, and
 * the errors cell links to the events filtered to this service, route AND errors — so an operator
 * clicks a number to see the events behind it. Each cell carries its exact/estimate confidence
 * ("est." for a sampled estimate) and shows an em dash, never a fabricated zero, for a null value.
 *
 * The explorer is reached by a path with filter query params (`/events?service=…&route=…`); the
 * events screen (task 11.12) reads them. Linking by query keeps the drill-down forward-compatible
 * before that screen is built, rather than inventing a bespoke route.
 */

type RouteStats = components["schemas"]["RouteStats"];

export interface RouteTableProps {
  readonly service: Service;
  readonly routes: readonly RouteStats[];
}

export function RouteTable(props: RouteTableProps): ReactNode {
  const navigate = useNavigate();

  // Busiest routes first, so the operator sees the heaviest traffic at the top. A null rate sorts
  // last (we cannot rank what we cannot measure).
  const rows = [...props.routes].sort((a, b) => rate(b) - rate(a));

  const goEvents = (route: string, errorsOnly: boolean): void => {
    const params = new URLSearchParams({ service: props.service, route });
    if (errorsOnly) params.set("outcome", "error");
    navigate(`/events?${params.toString()}`);
  };

  const columns: readonly Column<RouteStats>[] = [
    {
      key: "route",
      header: "Route",
      isRowHeader: true,
      cell: (r) => (
        <button
          type="button"
          className="svc-link"
          onClick={() => goEvents(r.route, false)}
          aria-label={`Open events for ${props.service} route ${r.route}`}
        >
          <code>{r.route}</code>
        </button>
      ),
    },
    ...ROUTE_SIGNALS.map(
      (spec): Column<RouteStats> => ({
        key: spec.key,
        header: `${spec.title}${spec.unit === "" ? "" : ` (${spec.unit.trim()})`}`,
        align: "right",
        cell: (r) => {
          const errorsCol = spec.key === "error_ratio";
          return (
            <SignalCell
              spec={spec}
              stats={r}
              onClick={errorsCol ? () => goEvents(r.route, true) : undefined}
            />
          );
        },
      }),
    ),
  ];

  return (
    <section className="svc-routes" aria-label="Routes">
      <h2 className="svc-section__title">Routes</h2>
      <DenseTable
        caption={`Golden signals per route for ${props.service}`}
        columns={columns}
        rows={rows}
        rowKey={(r) => r.route}
        stickyHeader
        emptyMessage="No routes have reported for this service in the current window."
      />
    </section>
  );
}

/** One signal cell: formatted value with its confidence suffix, optionally a drill-down button. */
function SignalCell(props: {
  spec: GoldenSignalSpec;
  stats: RouteStats;
  onClick?: (() => void) | undefined;
}): ReactNode {
  const q = props.stats.signals[props.spec.key];
  const measure = quantityMeasure(q);
  const text =
    q.value === null
      ? "n/a"
      : `${props.spec.format(q.value)}${props.spec.unit}${estimateSuffix(measure)}`;

  if (props.onClick !== undefined && q.value !== null) {
    return (
      <button
        type="button"
        className="svc-link"
        data-measure={measure}
        onClick={props.onClick}
        aria-label={`${props.spec.title} ${text} for route ${props.stats.route}. Open the events behind it`}
      >
        {text}
      </button>
    );
  }
  return (
    <span data-measure={measure} className="svc-num">
      {text}
    </span>
  );
}

/** The numeric traffic rate for sorting; a null rate sorts to the bottom. */
function rate(r: RouteStats): number {
  const v = r.signals.rate_per_s.value;
  return v === null ? -1 : v;
}
